export default () => ({
    isEditing: false,
    processing: true,
    defaultProfileId: null,
    activeProfileId: null,
    profiles: [],
    originalForm: {},
    errors: {},
    successMessage: '',
    isNewProfile: false,
    isDefault: false,
    showDeleteProfileModal: false,

    // Данные о доставке
    deliveryTypes: [],
    // selectedDeliveryTypeId: null,

    routes: {
        fetch: '',
        update: '',
        delete: '',
    },
    csrfToken: '',

    form: {
        label: '',
        first_name: '',
        last_name: '',
        phone: '',
        company: '',
        inn: '',
        delivery_type_id: '',
        city: '',
        address: '',
        comment_order: '',
    },

    setForm(data) {
        const defaults = {
            id: null,
            label: '',
            first_name: '',
            last_name: '',
            phone: '',
            company: '',
            inn: '',
            delivery_type_id: '',
            city: '',
            address: '',
            comment_order: '',
        };

        Object.assign(this.form, defaults, data);
    },

    async fetchProfiles() {
        try {
            const res = await fetch(this.routes.fetch, {
                headers: { Accept: 'application/json' },
            });
            if (res.ok) {
                const data = await res.json();
                this.defaultProfileId = data.default_profile_id;
                this.deliveryTypes = data.deliveryTypes;
                this.profiles = data.profiles ?? [];
                this.activeProfileId =
                    this.defaultProfileId ?? this.profiles[0]?.id;
                this.selectProfile(this.activeProfileId);
            }
        } catch (e) {
            console.error('Fetch profiles error:', e);
        } finally {
            this.processing = false;
        }
    },

    selectProfile(id) {
        const profile =
            this.profiles.find((p) => p.id === id) ?? this.profiles[0];
        if (profile) {
            this.activeProfileId = profile.id;
            this.setForm(profile);
            this.originalForm = { ...profile };
            this.isNewProfile = false;
            this.isDefault = profile.id === this.defaultProfileId;
            this.clearMessages();
        }
    },

    clearMessages() {
        this.errors = {};
        this.successMessage = '';
    },

    clearError(field) {
        if (this.hasError(field)) delete this.errors[field];
    },

    hasError(field) {
        return this.errors[field]?.length > 0;
    },

    getError(field) {
        return this.errors[field]?.[0] ?? '';
    },

    get requiresAddress() {
        const type = this.deliveryTypes.find(
            (d) => d.id === parseInt(this.form?.delivery_type_id),
        );
        return type ? type.with_address : false;
    },

    get csrfHeader() {
        return (
            document.querySelector('meta[name=csrf-token]')?.content ??
            this.csrfToken
        );
    },

    get deletingProfileName() {
        return this.form.label || 'Без названия';
    },

    async submit() {
        if (this.processing) return;

        const isDefault = this.isDefault;
        const { is_default, created_at, updated_at, user_id, ...formData } =
            this.form;

        this.processing = true;
        this.clearMessages();

        try {
            const res = await fetch(this.routes.update, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': this.csrfHeader,
                },
                body: JSON.stringify({
                    ...formData,
                    is_default: isDefault,
                }),
            });

            const data = await res.json();

            if (res.status === 422) {
                this.errors = data.errors ?? {};
                return;
            }

            if (!res.ok) {
                this.errors = {
                    _general: [data.message ?? 'Произошла ошибка'],
                };
                return;
            }

            if (data.profile) {
                const index = this.profiles.findIndex(
                    (p) => p.id === data.profile.id,
                );
                if (index !== -1) {
                    this.profiles[index] = { ...data.profile };
                } else {
                    this.profiles.push({ ...data.profile });
                }
                this.activeProfileId = data.profile.id;
                this.setForm(data.profile);
                this.originalForm = { ...data.profile };
                this.isNewProfile = false;
            }

            if (data.default_profile_id !== undefined) {
                this.defaultProfileId = data.default_profile_id;
                this.isDefault = this.activeProfileId === this.defaultProfileId;
            }

            this.successMessage = data.message ?? 'Сохранено';
            this.isEditing = false;

            setTimeout(() => (this.successMessage = ''), 3000);
        } catch (e) {
            console.error('Submit error:', e);
            this.errors = { _general: ['Ошибка сети. Попробуйте ещё раз.'] };
        } finally {
            this.processing = false;
        }
    },

    confirmDelete() {
        this.showDeleteProfileModal = true;
    },

    cancelDelete() {
        this.showDeleteProfileModal = false;
    },

    async deleteProfile() {
        if (!this.activeProfileId) return;
        if (this.processing) return;

        this.processing = true;
        this.showDeleteProfileModal = false;
        this.clearMessages();

        try {
            const res = await fetch(
                `${this.routes.delete}/${this.activeProfileId}`,
                {
                    method: 'DELETE',
                    headers: {
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': this.csrfHeader,
                    },
                },
            );

            const data = await res.json();

            if (!res.ok) {
                this.errors = {
                    _general: [data.message ?? 'Не удалось удалить профиль'],
                };
                return;
            }

            this.profiles = this.profiles.filter(
                (p) => p.id !== this.activeProfileId,
            );

            if (data.default_profile_id !== undefined) {
                this.defaultProfileId = data.default_profile_id;
            }

            const fallbackId = this.defaultProfileId ?? this.profiles[0]?.id;
            if (fallbackId) {
                this.selectProfile(fallbackId);
            } else {
                this.activeProfileId = null;

                this.setForm({});

                this.originalForm = {};
                this.isDefault = false;
            }

            this.isEditing = false;
            this.successMessage = data.message ?? 'Профиль удалён';
            setTimeout(() => (this.successMessage = ''), 3000);
        } catch (e) {
            console.error('Delete error:', e);
            this.errors = { _general: ['Ошибка сети. Попробуйте ещё раз.'] };
        } finally {
            this.processing = false;
        }
    },

    addProfile() {
        this.activeProfileId = null;
        this.setForm({});
        this.originalForm = {};
        this.isNewProfile = true;
        this.isDefault = false;
        this.isEditing = true;
        this.clearMessages();
    },

    cancel() {
        this.isEditing = false;
        this.clearMessages();

        if (this.isNewProfile) {
            const fallbackId = this.defaultProfileId ?? this.profiles[0]?.id;
            if (fallbackId) {
                this.selectProfile(fallbackId);
            }
            this.isNewProfile = false;
        } else {
            this.setForm(this.originalForm);
            this.isDefault = this.activeProfileId === this.defaultProfileId;
        }
    },
});
